import { Component } from 'react';
import { path } from 'ramda';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { WithContext as ReactTags } from 'react-tag-input';
import Tooltip from '../tooltip/Tooltip.jsx';
import s from './TagInput.module.scss';

const cssClassNames = {
    tags: s.tagInput,
    tagInput: s.textInputWrapper,
    tagInputField: s.tagInputField,
    selected: 'focusTarget',
    tag: s.tag,
    remove: s.remove,
    suggestions: s.suggestions,
    activeSuggestion: s.activeSuggestion,
};

const KeyCodes = {
    comma: 188,
    enter: 13,
};

export default class TagInput extends Component {
    static propTypes = {
        className: PropTypes.string,
        disabled: PropTypes.bool,
        maxItems: PropTypes.number,
        onChange: PropTypes.func,
        placeHolder: PropTypes.string,
        suggestions: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                text: PropTypes.string,
            }),
        ),
        tags: PropTypes.arrayOf(PropTypes.string),
    };

    static defaultProps = {
        className: '',
        disabled: false,
        onChange: () => {},
        placeHolder: '',
        suggestions: [],
        tags: [],
    };

    constructor(props) {
        super(props);
        this.state = { inputFocus: false };
    }

    getClassNames = () => {
        const activeClass = this.state.inputFocus ? s.active : '';
        const topLevelClassName = `${s.tagInput} ${activeClass}`;
        return { ...cssClassNames, tags: topLevelClassName };
    };

    getTagObjectForId = id => {
        const { suggestions } = this.props;
        const tagObject = suggestions.find(suggestion => suggestion.id === id);
        return tagObject || { id, text: id };
    };

    addSingleTag = id => {
        const trimmedId = id.trim();
        if (!trimmedId) return; // reject empty/whitespace-only tags
        const { tags, onChange } = this.props;
        if (tags.some(tag => tag.toLowerCase() === trimmedId.toLowerCase())) {
            // don't add duplicates
            return;
        }
        tags.push(trimmedId);
        onChange(tags);
    };

    // If the user pastes a string with commas in it, a single tag object
    // get submitted here- we need to handle that
    handleAddition = newTagObject => {
        if (this.isMaxedOut()) {
            return;
        }

        if (!newTagObject.id.includes(',')) {
            this.addSingleTag(newTagObject.id);
            return;
        }

        const existingTags = this.props.tags;
        const tagString = newTagObject.id;
        const newTagIds = tagString.split(',').map(tag => tag.trim()).filter(Boolean);
        const dedupedNewIds = newTagIds.filter(id => !existingTags.includes(id));
        const sizeLimitedNewIds = [...existingTags, ...dedupedNewIds].slice(0, this.props.maxItems);
        this.props.onChange(sizeLimitedNewIds);
    };

    handleDeletion = index => {
        const newTags = this.props.tags.slice();
        newTags.splice(index, 1);
        this.props.onChange(newTags);
    };

    handleDrag = (tagObject, oldIndex, newIndex) => {
        const newTags = this.props.tags.slice();
        newTags.splice(oldIndex, 1);
        newTags.splice(newIndex, 0, tagObject.id);
        this.props.onChange(newTags);
    };

    isTagAdded = id => {
        const { tags } = this.props;
        return tags.find(tag => tag.toLowerCase() === id.toLowerCase());
    };

    getTextInputValue = () => path(['reactTags', 'child', 'textInput', 'value'], this);

    setTextInputValue = v => {
        const textInput = path(['reactTags', 'child', 'textInput'], this);
        if (textInput) textInput.value = v;
    };

    textInputFocus = () => {
        const textInput = path(['reactTags', 'child', 'textInput'], this);
        if (textInput) textInput.focus();
    };

    handleFilterSuggestions = (textInputValue, possibleSuggestionsArray) => {
        const match = textInputValue.toLowerCase();
        const matches = possibleSuggestionsArray.filter(item => item.id.toLowerCase().startsWith(match) && !this.isTagAdded(item.id));
        const filteredList = matches.slice(0, 10);

        // throw in an "Add tag: foo" item at the top if appropriate
        const currentInput = this.getTextInputValue();
        if (currentInput && !filteredList.find(item => item.id.toLowerCase() === currentInput.toLowerCase())) {
            const addSuggestion = { id: currentInput, text: `Create tag: "${currentInput}"` };
            filteredList.unshift(addSuggestion);
        }

        return filteredList;
    };

    // check for manually typed commas and create new tags out of
    // everything to the left of the comma, leaving the remainder in
    // the text input
    handleInputChange = value => {
        if (this.isMaxedOut()) {
            this.setTextInputValue('');
        }
        if (!value || value.indexOf(',') === -1) {
            return;
        }
        const tagArray = value.split(',');
        const lastItem = tagArray[tagArray.length - 1];
        const valuesToSave = tagArray.slice(0, -1);
        this.handleAddition({ id: valuesToSave.join(',') });
        this.setTextInputValue(lastItem);
    };

    handleClick = e => {
        if (e.target.className === 'focusTarget') {
            this.textInputFocus();
        }
    };

    handleCopyClick = () => {
        this.copyInput.select();
        document.execCommand('copy');
    };

    isMaxedOut = () => {
        const { maxItems, tags } = this.props;
        return maxItems && tags.length >= maxItems;
    };

    render() {
        const { tags, suggestions, className, placeHolder, disabled } = this.props;
        const tagObjects = tags.map(this.getTagObjectForId);
        const copyableValues = tags.join(',');
        const showAsActive = this.state.inputFocus && !this.isMaxedOut();
        const showPlaceholder = tags.length === 0;
        const classes = classNames(s.tagInputWrapper, className, { [s.active]: showAsActive }, { [s.maxedOut]: this.isMaxedOut() }, { [s.bigInput]: showPlaceholder });
        return (
            <div className={classes} onClick={this.handleClick}>
                <ReactTags
                    ref={elt => {
                        this.reactTags = elt;
                    }}
                    tags={tagObjects}
                    suggestions={suggestions}
                    handleAddition={this.handleAddition}
                    handleDelete={this.handleDeletion}
                    handleDrag={this.handleDrag}
                    delimiters={[KeyCodes.enter]}
                    handleInputChange={this.handleInputChange}
                    handleInputFocus={() => this.setState({ inputFocus: true })}
                    handleInputBlur={() => this.setState({ inputFocus: false })}
                    minQueryLength={1}
                    classNames={cssClassNames}
                    inline
                    placeholder={showPlaceholder ? placeHolder || 'Use commas to separate tags' : ''}
                    autofocus={false}
                    handleFilterSuggestions={this.handleFilterSuggestions}
                    readOnly={disabled}
                />
                <div className={s.copyButton} onClick={this.handleCopyClick}>
                    <div className={classNames('icon-copy', s.copyIcon)}>
                        <Tooltip arrowPosition="top" text="Copy Tags" />
                    </div>
                </div>
                <input
                    className={s.copyableInput}
                    value={copyableValues}
                    readOnly
                    ref={elt => {
                        this.copyInput = elt;
                    }}
                />
            </div>
        );
    }
}
